import React, { useState, useEffect } from 'react';
import { DataState } from '../../context/DataProvider';
import { BsRobot } from 'react-icons/bs';
import { FiChevronRight, FiArrowLeft, FiHome, FiGitCommit, FiInfo } from 'react-icons/fi';
import { RxCross2 } from 'react-icons/rx';
import toast from 'react-hot-toast';
import { useAgent } from '../../context/AgentProvider';
import { updateAgentSequence } from '../../services/api';
import { ImSpinner2 } from 'react-icons/im'; // A common icon for loading spinners

// A reusable UI component to display a path array. It is self-contained and responsive.
const PathDisplay = ({ path }) => (
  <div className="flex items-center flex-wrap gap-x-2 gap-y-1 p-3 bg-zinc-800/50 rounded-lg text-sm border border-zinc-700 min-h-[44px]">
    {path.map((agent, index) => (
      <React.Fragment key={index}>
        <span className="font-mono">{agent}</span>
        {index < path.length - 1 && <FiChevronRight className="text-zinc-500 flex-shrink-0" />}
      </React.Fragment>
    ))}
  </div>
);

// The core pathfinding algorithm, unchanged but proven correct.
const findTraversalPath = (startSequence, endSequence) => {
  if (JSON.stringify(startSequence) === JSON.stringify(endSequence)) {
    return [startSequence[startSequence.length - 1]];
  }
  let lcaIndex = -1;
  const minLength = Math.min(startSequence.length, endSequence.length);
  for (let i = 0; i < minLength; i++) {
    if (startSequence[i] === endSequence[i]) {
      lcaIndex = i;
    } else {
      break;
    }
  }
  const pathUp = startSequence.slice(lcaIndex).reverse();
  const pathDown = endSequence.slice(lcaIndex + 1);
  return [...pathUp, ...pathDown];
};


const AgentArchitectureModal = ({ isOpen, onClose }) => {
  const { agentSequence, setAgentSequence, agentTraversalPaths, setAgentTraversalPaths, currentTaskId, setTasks, userGoogleId: accessToken } = DataState();
  const {agentTree, agentDescriptions} = useAgent();

  const [localSequence, setLocalSequence] = useState([]);
  const [traversalPath, setTraversalPath] = useState([]);

  // NEW: Local loading state for the confirmation action
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalSequence(agentSequence);
    }
  }, [isOpen, agentSequence]);

  useEffect(() => {
    if (!isOpen) return;
    const path = findTraversalPath(agentSequence, localSequence);
    setTraversalPath(path);
  }, [localSequence, agentSequence, isOpen]);

  if (!isOpen) return null;

  // UPDATED: handleConfirm is now an async function to handle the API call
  const handleConfirm = async () => {
    // Prevent function execution if button is disabled
    if (!hasPathChanged || isConfirming) return;
    
    setIsConfirming(true);
    
    try {
      // Only call the API if a task ID exists
      if (currentTaskId) {
        await updateAgentSequence(accessToken, currentTaskId, localSequence);
        setTasks((tasks) => tasks.map((task) => (task._id === currentTaskId ? {
        ...task,
        agent_sequence: localSequence,
        is_agent_mode:true
      } : task)))
      }
      
      // Update local state only after successful API call (or if no task ID)
      if (traversalPath.length > 1) {
        setAgentTraversalPaths([...agentTraversalPaths, traversalPath]);
      }
      setAgentSequence(localSequence);
      
      toast.success("Agent path confirmed and saved!");
      onClose();

    } catch (error) {
      console.error("Failed to update agent sequence:", error);
      toast.error("Could not save agent path. Please try again.");
      // Do not close the modal on error, so the user can retry.
    } finally {
      // Always stop the loading state
      setIsConfirming(false);
    }
  };
  
  const handleCancel = () => onClose();
  const handleSelectAgent = (agentName) => setLocalSequence([...localSequence, agentName]);
  const handleGoBack = () => {
    if (localSequence.length > 1) setLocalSequence(localSequence.slice(0, -1));
  };
  const handleBreadcrumbClick = (index) => setLocalSequence(localSequence.slice(0, index + 1));
  
  const currentAgent = localSequence[localSequence.length - 1];
  const childrenAgents = agentTree[currentAgent] || [];
  const hasPathChanged = JSON.stringify(localSequence) !== JSON.stringify(agentSequence);
  const isActualTraversal = traversalPath.length > 1;

  return (
    // **RESPONSIVE CHANGE**: Modal container is now responsive.
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      {/* Manages width and scrolling for different screen sizes */}
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl w-full max-w-lg md:max-w-2xl lg:max-w-3xl max-h-[90vh] flex flex-col animate-fadeIn">
        
        <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <BsRobot className="text-xl text-blue-400" />
            <h2 className="text-lg font-semibold">Agent Architecture</h2>
          </div>
          <button onClick={handleCancel} className="text-zinc-400 hover:text-white transition-colors p-1 rounded-full hover:bg-zinc-700">
            <RxCross2 size={20} />
          </button>
        </div>

        {/* **RESPONSIVE CHANGE**: Main content area is now scrollable on small screens. */}
        <div className="flex-grow p-4 md:p-6 overflow-y-auto">
          <div className="flex items-center flex-wrap gap-2 mb-6 text-sm text-zinc-400">
            {localSequence.map((agent, index) => (
              <React.Fragment key={agent + index}>
                <button
                  onClick={() => handleBreadcrumbClick(index)}
                  className={`hover:text-white transition-colors ${index === localSequence.length - 1 ? 'text-white font-medium' : ''}`}
                >
                  {agent === "Orchestrator" ? <FiHome className="inline-block mb-1" /> : agent}
                </button>
                {index < localSequence.length - 1 && <FiChevronRight className="flex-shrink-0"/>}
              </React.Fragment>
            ))}
          </div>
          
          {/* **RESPONSIVE CHANGE**: This layout stacks vertically on mobile and goes horizontal on medium screens and up. */}
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/3 flex-shrink-0 flex flex-col items-center gap-3">
              <button
                onClick={handleGoBack}
                disabled={localSequence.length <= 1}
                className="w-full flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-all"
              >
                <FiArrowLeft />
                <span>Back</span>
              </button>
              <div className="w-full p-4 bg-blue-900/50 border border-[#5652CF] rounded-lg text-center">
                <BsRobot className="mx-auto text-3xl mb-2 text-blue-300" />
                <p className="font-bold text-lg">{currentAgent}</p>
                <p className="text-xs text-zinc-300">Selected Agent</p>
              </div>
            </div>

            <div className="w-full md:w-2/3 flex-grow">
              <h3 className="text-md font-semibold text-zinc-300 mb-3">Select Next Agent:</h3>
              {/* **RESPONSIVE CHANGE**: Grid stacks to 1 column on small phones, 2 on larger ones. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {childrenAgents.length > 0 ? (
                  childrenAgents.map((child) => (
                    <button key={child} onClick={() => handleSelectAgent(child)} className="group p-4 bg-zinc-800 rounded-lg text-left hover:bg-zinc-700 hover:border-zinc-500 border border-transparent transition-all">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{child}</span>
                        <FiChevronRight className="text-zinc-500 group-hover:text-white transition-transform duration-200 group-hover:translate-x-1" />
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="col-span-1 sm:col-span-2 text-center p-4 bg-zinc-800/50 rounded-lg text-zinc-400">
                    <p>No further agents in this path.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-4 border-t border-zinc-800">
             <div className="flex items-center gap-2 mb-3 text-zinc-300">
                <FiGitCommit />
                <h3 className="text-md font-semibold">Traversal Path</h3>
            </div>
            {/* **LOGIC CHANGE**: Conditionally show the path or a "no traversal" message. */}
            {isActualTraversal ? (
                <PathDisplay path={traversalPath} />
            ) : (
                <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg text-sm text-zinc-400 border border-zinc-700">
                    <FiInfo className="flex-shrink-0" />
                    <span>No traversal needed. The selected agent is the current active agent.</span>
                </div>
            )}
          </div>
        </div>

        {/* UPDATED: Footer buttons now reflect the loading state */}
        <div className="flex-shrink-0 flex justify-end items-center gap-4 p-4 bg-zinc-900/50 border-t border-zinc-800 rounded-b-2xl">
          <button onClick={handleCancel} className="bg-zinc-700 hover:bg-zinc-600 text-white font-semibold py-2 px-5 rounded-lg transition-all">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!hasPathChanged || isConfirming}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800/50 disabled:cursor-not-allowed text-white font-semibold py-2 px-5 rounded-lg transition-all flex items-center justify-center gap-2 w-40"
          >
            {isConfirming ? (
              <>
                <ImSpinner2 className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>Confirm Selection</span>
            )}
          </button>
        </div>
      </div>
       <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.2s ease-out forwards;
          }
        `}</style>
    </div>
  );
};

export default AgentArchitectureModal;